/*
 * THE RAIL'S WORDS: what People, Fit, Their LinkedIn and intake, Behind the scenes
 * and Deal and money say, worked out once and tested here.
 *
 * The rail is reference, not news (the brief, "Who owns what"), so every group is
 * first of all ONE LINE that carries its point on its own ("Shahar is point of
 * contact · 1 duplicate", "Would score 33 today · computer network security · 9
 * people · Israel", "Nothing acts on Pluto Security now"), and only then a body.
 * Both are derived from the payloads the shell already holds; nothing here fetches,
 * and nothing here decides a situation (record/situation.mjs does, once).
 *
 * WHERE A FRAME'S WORDS COULD NOT BE BACKED, the reality check's resolution is
 * what is written (scratchpad/check/reality.txt), and the line says so beside it:
 *   [10] the referrer is the sender of the intro row, never someone we guess at;
 *   [20] Behind the scenes lists only what can act now, from the server's
 *        `applies`, and says "only answers the website form" rather than "if
 *        they write";
 *   [22] the duplicate warning prints the STORED reason, in words;
 *   the brief: LinkedIn shows the ad account and our ads' reach, never spend.
 *
 * Pure: `now` is a parameter, so the tests pin the day.
 */
import { shortDate, plural, fullName, firstName, enumWords, moneyShort, sentence, toDate } from "./format.mjs";
import { FIELDS as PROFILE_FIELDS } from "./profileModel.mjs";
import { formAnswers } from "../formAnswers.mjs";

const blank = (v) => v === null || v === undefined || (typeof v === "string" && !v.trim());
const idOf = (v) => (v === null || v === undefined ? null : String(v));

/*
 * WHOSE SITUATION THE RAIL ANSWERS TO. On an associated colleague's record the
 * page below the pointer is the COMPANY's record (situation.mjs recordView), so a
 * group that leaves out what the lead block shows must ask the company's situation,
 * not "associated": Gali's record shows Pluto's New client block, fee and all.
 */
export function railSituation(view) {
  if (!view) return null;
  const s = view.situation || {};
  return view.key === "associated" && s.company_situation && s.company_situation !== "out" ? s.company_situation : view.key;
}

/* ================================================================== people */

/*
 * LIFECYCLE, AS THE PILL SAYS IT. The five a person is moved between by hand, in
 * the order a deal moves them; the rest (subscriber, evangelist, other) are read
 * but never offered, because nothing in the sales process moves anyone there.
 */
export const LIFECYCLE_CHOICES = [
  ["lead", "lead"],
  ["marketingqualifiedlead", "MQL"],
  ["salesqualifiedlead", "SQL"],
  ["opportunity", "opportunity"],
  ["customer", "customer"],
];
const LIFECYCLE_WORDS = { ...Object.fromEntries(LIFECYCLE_CHOICES), subscriber: "subscriber", evangelist: "evangelist", other: "other" };
const LIFECYCLE_CLASS = { lead: "lead", marketingqualifiedlead: "mql", salesqualifiedlead: "sql", opportunity: "opportunity", customer: "customer" };

/**
 * The pill: { stage, word, cls, derived }. Anyone at a churned company reads
 * "churned" (memory, 2026-09-30: derived, the stored lifecycle untouched), so the
 * pill says what the relationship is while the menu still edits the column.
 */
export function lifecyclePill(person, company = null) {
  const stage = (person && person.lifecycle_stage) || null;
  if (company && company.status === "churned") return { stage, word: "churned", cls: "churned", derived: true };
  return { stage, word: LIFECYCLE_WORDS[stage] || (stage ? enumWords(stage) : "lead"), cls: LIFECYCLE_CLASS[stage] || "lead", derived: false };
}

/*
 * WHY TWO RECORDS LOOK LIKE ONE PERSON, in words. The dedupe writes its reasons as
 * phrases for a log ("same name at same company, different email"); the warning
 * reads them to a person (reality.txt [22]: "same name, two emails"). An unknown
 * phrase is printed as stored rather than paraphrased into something it never said.
 */
const DUPE_REASONS = [
  [/^same name at same company, different email(, after a company merge)?$/i, (m) => `same name, two emails${m[1] ? ", after a company merge" : ""}`],
];
export function dupeReasonWords(reason) {
  const s = String(reason || "").trim();
  if (!s) return null;
  for (const [re, words] of DUPE_REASONS) {
    const m = s.match(re);
    if (m) return words(m);
  }
  return s;
}

/**
 * The red box's lines, one per unresolved pair touching the company's people:
 *   "Shachar Eschel has two records: same name, two emails. Flagged Sep 16."
 * Two different names read as "may be one person". [{ key, ids, name, text }]
 */
export function duplicateLines(company, { now = new Date() } = {}) {
  const pairs = (company && company.duplicates) || [];
  const people = (company && company.contacts) || [];
  const nameOf = (id, fallback) => fullName(people.find((p) => idOf(p.id) === idOf(id))) || fallback || null;
  return pairs.map((d) => {
    const a = nameOf(d.a_contact_id, d.a_name);
    const b = nameOf(d.b_contact_id, d.b_name);
    const why = dupeReasonWords(d.reason);
    const flagged = d.created_at ? ` Flagged ${shortDate(d.created_at, { now })}.` : "";
    const head = a && b && a !== b ? `${a} and ${b} may be one person` : `${a || b || "Someone"} has two records`;
    return { key: `${d.a_contact_id}:${d.b_contact_id}`, ids: [idOf(d.a_contact_id), idOf(d.b_contact_id)], name: a || b || null, head, text: `${why ? `: ${why}.` : "."}${flagged}` };
  });
}

/**
 * EVERYONE AT THE COMPANY, ONCE EACH, the point of contact first.
 *
 *   peopleList({ company, contact, recordId })
 *     → { people: [{ person, isPoc, onScreen, dupe }], count, poc }
 *
 * Junk contacts are left out (they are never anyone's colleague). A suspected
 * duplicate pair shows as ONE row flagged "2 records" (frame 1a), keeping the twin
 * that is on screen or the point of contact, else the older one: the red box above
 * says there are two, and two identical rows would only say it again. `count` is
 * the people at the company with the pair folded, never an outsider (reality.txt
 * [10]: "4 people" mixed the referrer in with three Pluto people).
 */
export function peopleList({ company = null, contact = null, recordId = null } = {}) {
  const all = ((company && company.contacts) || (contact ? [contact] : [])).filter((c) => c && c.kind !== "junk");
  const pocId = idOf(company && company.primary_contact_id);
  const here = idOf(recordId || (contact && contact.id));
  const ids = new Set(all.map((c) => idOf(c.id)));

  const dropped = new Set();
  const flagged = new Set();
  for (const d of (company && company.duplicates) || []) {
    const a = idOf(d.a_contact_id);
    const b = idOf(d.b_contact_id);
    if (!ids.has(a) || !ids.has(b) || dropped.has(a) || dropped.has(b)) continue;
    const keepB = b === here || (b === pocId && a !== here);
    const keep = keepB ? b : a;
    dropped.add(keepB ? a : b);
    flagged.add(keep);
  }

  const people = all
    .filter((c) => !dropped.has(idOf(c.id)))
    .map((c, i) => ({ person: c, isPoc: idOf(c.id) === pocId, onScreen: idOf(c.id) === here, dupe: flagged.has(idOf(c.id)), i }))
    .sort((x, y) => (y.isPoc - x.isPoc) || (y.onScreen - x.onScreen) || x.i - y.i)
    .map(({ i, ...rest }) => rest);
  const poc = people.find((p) => p.isPoc) || null;
  return { people, count: people.length, poc: poc ? poc.person : null };
}

/*
 * THE REFERRER, an outsider shown beside the company's people (frame 1a, Dani).
 * Read off the intro email itself: a warm intro is copied onto the person it
 * introduces with `introduced_by`, and the record timeline names its real sender
 * (PR #156). Whoever that sender is, if they are not one of the company's own
 * people, they are the referrer. Nobody is guessed from a name.
 */
export function referrerOf({ timeline = [], company = null } = {}) {
  const ids = new Set(((company && company.contacts) || []).map((c) => idOf(c.id)));
  const intro = (timeline || [])
    .filter((r) => r && r.subtype === "email" && r.metadata && r.metadata.introduced_by && r.sender && r.sender.contact_id)
    .sort((a, b) => toDate(a.occurred_at) - toDate(b.occurred_at))[0];
  if (!intro || ids.has(idOf(intro.sender.contact_id))) return null;
  const s = intro.sender;
  return { contact_id: idOf(s.contact_id), name: fullName(s) || s.email || null, first: firstName(s), email: s.email || null, at: intro.occurred_at };
}

/**
 * People's one line:
 *   "Shahar is point of contact · 1 duplicate"           (frame 1a)
 *   "Dana is point of contact · lead · Add a person"     (1c: one person, a new lead)
 *   "Marco is point of contact · SQL"                    (1g: one person)
 *   "Gali is associated · Shahar is point of contact"    (an associated colleague)
 * The lifecycle word only when there is one person, because then it is the whole
 * story; "Add a person" only where the matrix asks for it (its New lead cell).
 */
export function peopleSummary({ list, contact = null, company = null, dupes = 0, line = "", associated = false } = {}) {
  const l = list || { people: [], count: 0, poc: null };
  if (!company) return [firstName(contact) || "This person", "no company"].join(" · ");
  const parts = [];
  if (associated && contact && (!l.poc || idOf(l.poc.id) !== idOf(contact.id))) parts.push(`${firstName(contact)} is associated`);
  parts.push(l.poc ? `${firstName(l.poc)} is point of contact` : "No point of contact");
  if (l.count === 1 && l.poc) parts.push(lifecyclePill(l.poc, company).word);
  if (dupes) parts.push(plural(dupes, "duplicate"));
  if (l.count <= 1 && /Add a person/.test(String(line || ""))) parts.push("Add a person");
  return parts.join(" · ");
}

/*
 * What "Enrich from Apollo" came back with when it came back with nothing. Every
 * one is an ANSWER, not a failure (the old record's ENRICH_MISSES, kept word for
 * word except the dash): the button asked Apollo a question and this is the reply.
 */
export const ENRICH_MISSES = {
  no_match: "Apollo has no match for this contact.",
  nothing_to_fill: "Apollo has nothing this record is missing.",
  mailer_domain: "That domain is a mailer, not a company, so Apollo can’t match it.",
  no_match_key: "Needs an email, or a name and a company, to look anyone up.",
  no_api_key: "Apollo isn’t configured on the server.",
  not_a_lead: "Apollo only looks up leads.",
  error: "The Apollo lookup failed. Try again.",
};
export function enrichWords(result) {
  if (!result) return ENRICH_MISSES.error;
  // Said even when their LinkedIn did fill: the company facts were held back on purpose.
  if (result.reason === "other_employer" && result.other_employer) {
    return `Apollo still has them at ${result.other_employer}, so nothing about that company or their title was copied.`;
  }
  if (result.enriched) return null;
  return ENRICH_MISSES[result.reason] || ENRICH_MISSES.no_match;
}

/** "+972 54 555 0101" → { tel: "+972545550101", wa: "https://wa.me/972545550101" }. */
export function phoneLinks(phone) {
  const raw = String(phone || "").trim();
  if (!raw) return null;
  const digits = raw.replace(/[^0-9+]/g, "").replace(/(?!^)\+/g, "");
  if (digits.replace(/\+/g, "").length < 5) return { tel: null, wa: null };
  return { tel: `tel:${digits}`, wa: `https://wa.me/${digits.replace(/^\+/, "")}` };
}

/** "Shahar Bahat" → { first_name: "Shahar", last_name: "Bahat" }; the rest is the last name. */
export function splitName(text) {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return { first_name: null, last_name: null };
  return { first_name: words[0], last_name: words.slice(1).join(" ") || null };
}

/* ================================================================== fit */

/** "$4M", "4,000,000", "4.5m", "500k" → a number; null for anything else. */
export function parseMoney(text) {
  const s = String(text === null || text === undefined ? "" : text).trim().toLowerCase().replace(/[$€£₪,\s]/g, "");
  const m = s.match(/^(\d+(?:\.\d+)?)([kmb])?$/);
  if (!m) return null;
  const mult = { k: 1e3, m: 1e6, b: 1e9 }[m[2]] || 1;
  const n = Math.round(Number(m[1]) * mult);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** A founding year: four digits, not in the future, not before 1800. */
export function parseYear(text, now = new Date()) {
  const s = String(text === null || text === undefined ? "" : text).trim();
  if (!/^\d{4}$/.test(s)) return null;
  const y = Number(s);
  return y >= 1800 && y <= (toDate(now) || new Date()).getFullYear() ? y : null;
}

/*
 * FUNDING, FROM BOTH KEYS. Apollo's firmographics write `total_funding`; the
 * HubSpot import wrote `total_money_raised`; the old card read only the first and
 * printed nothing for a company that had the second. The stage ("Series B+") is
 * not a stored fact at all: the scorer derives it from the amount (score.js
 * fundingStage), and its funding chip carries what it read, so the rail repeats
 * the scorer instead of keeping a second table of thresholds.
 */
export function fundingOf(company, leadScore = null) {
  const p = (company && company.properties) || {};
  const raw = !blank(p.total_funding) ? p.total_funding : !blank(p.total_money_raised) ? p.total_money_raised : null;
  const n = raw === null ? null : Number(raw);
  const amount = n !== null && Number.isFinite(n) && n > 0 ? n : null;
  const chip = ((leadScore && leadScore.chips) || []).find((c) => c && c.group === "funding");
  const stage = chip && !chip.unknown && typeof chip.fact === "string" && chip.fact.trim() ? chip.fact.trim() : null;
  const text = [stage, amount !== null ? moneyShort(amount) : raw !== null ? String(raw) : null].filter(Boolean).join(" · ");
  return { amount, stage, text: text || null };
}

/**
 * Fit, body and line:
 *   fitView({ company, score, leadScore, prominence })
 *     → { summary, rows: [{ key, label, value, add?, breakdown?, sub? }] }
 *
 * The score is in the rail only where it has left the header (the brief: clients,
 * churned, lost keep it in Fit as "Would score 33 today"; an associated colleague's
 * record says whose score it is). Where the header already features it, the group
 * leaves it out, as every group leaves out what is shown above it.
 *
 * Industry is the STORED enum in words ("computer network security"), never the
 * scoring row it matched ("Cybersecurity"): that row belongs to the breakdown, and
 * printing it here would state a category nobody stored (the build map's risk).
 *
 * Unknowns that properties_set can write (PR #154's allowlist) read "Add {thing}";
 * the column-backed ones (industry, headcount, country) read "not recorded" and
 * are edited on Profile, where the industry editor offers the stored values only.
 */
export function fitView({ company = null, score = null, leadScore = null, prominence = null } = {}) {
  const co = company || {};
  const p = co.properties || {};
  const s = score && score.show ? score : null;
  const rows = [];
  const fitOnly = prominence === "fit_only";
  const scoredOn = prominence === "scored_on";

  if (s && fitOnly) rows.push({ key: "would", label: "Would score", value: s.label, breakdown: true });
  if (s && scoredOn) rows.push({ key: "score", label: "Score", value: s.label, breakdown: true, sub: s.scoredOn || null });

  const industry = co.industry ? enumWords(co.industry) : null;
  rows.push({ key: "industry", label: "Industry", value: industry });
  rows.push({ key: "employees", label: "Employees", value: blank(co.employee_range) ? null : String(co.employee_range) });

  const funding = fundingOf(co, leadScore);
  rows.push({ key: "funding", label: "Funding", value: funding.text, add: funding.text ? null : { prop: "total_funding", label: "Add funding", parse: "money", placeholder: "$4M" } });

  const city = blank(p.city) ? null : String(p.city);
  const place = [city, blank(co.country) ? null : co.country].filter(Boolean).join(", ");
  rows.push({ key: "location", label: city ? "Location" : "Country", value: place || null, add: place ? null : { prop: "city", label: "Add city", parse: "text", placeholder: "Boston" } });

  rows.push({ key: "founded", label: "Founded", value: blank(p.founded_year) ? null : String(p.founded_year), add: blank(p.founded_year) ? { prop: "founded_year", label: "Add founded year", parse: "year", placeholder: "2019" } : null });

  const description = !blank(p.company_description) ? p.company_description : !blank(p.description) ? p.description : null;
  rows.push({ key: "description", label: "Description", value: description, add: description ? null : { prop: "company_description", label: "Add description", parse: "text", placeholder: "What they do, in a line" } });

  const site = co.domain || p.company_website || null;
  rows.push({ key: "website", label: "Website", value: site ? String(site).replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/+$/, "") : null, add: site ? null : { prop: "company_website", label: "Add website", parse: "text", placeholder: "example.com" } });

  const lead = s ? (fitOnly ? (s.blocked ? "Would score 0 today, blocked" : `Would score ${s.score} today`) : prominence === "small" || scoredOn ? s.label : null) : null;
  const people = blank(co.employee_range) ? null : `${co.employee_range} people`;
  const parts = [lead, industry, people, funding.stage, city || co.country || null].filter(Boolean);
  if (parts.length && !lead) parts[0] = sentence(parts[0]);
  return { summary: parts.join(" · "), rows };
}

/* ================================================================== linkedin and intake */

const CHANNEL_WORDS = { linkedin_dm: "LinkedIn DM", whatsapp_dm: "WhatsApp DM", other: "Added by hand" };

/** The form's own name ("Get a proposal"), the generic "Website form" otherwise. */
export function formTitle(form) {
  const meta = (form && form.metadata) || {};
  const named = (meta.form && (meta.form.form_name || meta.form.name)) || meta.form_name || null;
  if (named) return String(named);
  return form && form.title && String(form.title).trim() ? String(form.title).trim() : "Website form";
}

/**
 * HOW THEY ARRIVED, one row: { label, text, short }.
 *   form   "Form · Get a proposal · Sep 4"           short "form Sep 4"
 *   intro  "Intake · Intro email from Dani Avitz, Sep 15 · no form"   short "intro Sep 15"
 *   a DM   "Intake · LinkedIn DM, Sep 4 · no form"
 * An intro reads "no form" because the warm-intro triage writes referral keys in a
 * form's shape (formAnswers.mjs), and a person reading "Referred: yes" would think
 * someone filled something in.
 */
export function intakeLine({ contact = null, form = null, now = new Date() } = {}) {
  const c = contact || {};
  if (form) {
    const day = shortDate(form.occurred_at, { now });
    return { label: "Form", text: `${formTitle(form)} · ${day}`, short: `form ${day}` };
  }
  const day = c.created_at ? shortDate(c.created_at, { now }) : null;
  if (c.lead_channel === "intro_email") {
    return {
      label: "Intake",
      text: `Intro email${c.referrer_name ? ` from ${c.referrer_name}` : ""}${day ? `, ${day}` : ""} · no form`,
      short: day ? `intro ${day}` : "intro",
    };
  }
  const channel = CHANNEL_WORDS[c.lead_channel];
  if (channel) return { label: "Intake", text: `${channel}${day ? `, ${day}` : ""} · no form`, short: day ? `${channel.charAt(0).toLowerCase()}${channel.slice(1)} ${day}` : null };
  if (day) return { label: "Intake", text: `Arrived ${day} · no form on record`, short: null };
  return { label: "Intake", text: "not recorded", short: null };
}

const commas = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);

/** "$40K/mo", "€12K/mo", "40K CAD/mo": a budget figure with its currency, never a sum across two. */
function budgetWords(amount, currency) {
  if (amount === null || amount === undefined || !Number.isFinite(Number(amount))) return null;
  const known = ["USD", "EUR", "GBP", "ILS"].includes(currency || "USD");
  const v = moneyShort(Number(amount), currency || "USD");
  return known ? `${v}/mo` : `${v} ${currency}/mo`;
}

/** "Jun 2026", "Jun–Sep 2026", "Dec 2025–Feb 2026": the delivery archive's month grain. */
export function monthSpan(first, last) {
  const name = (m) => {
    const [y, mm] = String(m || "").split("-");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return y && months[Number(mm) - 1] ? { y, m: months[Number(mm) - 1] } : null;
  };
  const a = name(first);
  const b = name(last);
  if (!a) return b ? `${b.m} ${b.y}` : null;
  if (!b || (a.y === b.y && a.m === b.m)) return `${a.m} ${a.y}`;
  return a.y === b.y ? `${a.m}–${b.m} ${b.y}` : `${a.m} ${a.y}–${b.m} ${b.y}`;
}

/**
 * Their LinkedIn and intake:
 *   linkedinView({ act, contact, form, situation, now })
 *     → { state, summary, accountRows, reachRows, hubspotRows, intake }
 *
 * state is one of
 *   not_connected  our auditor is not connected (AUDITOR_DATABASE_URL unset: true in
 *                  production today), so nothing about their account is knowable;
 *   no_account     connected, and no ad account in it resolves to this company;
 *   linked         act.ads exists.
 * The first two must not read the same: "we can't see" is not "there is nothing".
 *
 * NEVER SPEND. The ads entry carries the last audit's spend and the delivery
 * archive a pro-rata cost; neither is printed. The account's budget, its live
 * campaigns, its last change and the audit grade are; our reach is impressions,
 * clicks, engagements and leads by month.
 *
 * The line leaves the intake out where the lead block already is the intake (a
 * new lead's form, frame 1c), and says "Campaign: not captured" when there is no
 * intake to name (1g).
 */
export function linkedinView({ act = null, contact = null, form = null, situation = null, now = new Date() } = {}) {
  const ads = (act && act.ads) || null;
  const delivery = act && act.delivery && act.delivery.impressions ? act.delivery : null;
  const state = ads ? "linked" : act && act.auditor_connected ? "no_account" : "not_connected";
  const running = form ? (formAnswers(form).find((a) => a.label === "Running ads") || {}).value : null;
  const intake = intakeLine({ contact, form, now });

  const accountRows = [];
  if (ads) {
    const n = (ads.accounts || []).length;
    accountRows.push({ key: "account", label: "Ad account", value: n > 1 ? `${n} accounts` : "Linked" });
    const budget = ads.mixed_currency
      ? (ads.accounts || []).map((a) => budgetWords(a.monthly_budget, a.currency)).filter(Boolean).join(" + ") || null
      : budgetWords(ads.monthly_budget, ads.currency);
    if (budget) accountRows.push({ key: "budget", label: "Their budget", value: `${budget} in the account` });
    const live = Number(ads.active_campaigns) || 0;
    const paused = Number(ads.paused_campaigns) || 0;
    accountRows.push({ key: "campaigns", label: "Campaigns", value: live ? `${live} live` : paused ? `nothing live, ${paused} paused` : "none live" });
    if (ads.last_change_at) accountRows.push({ key: "changed", label: "Last change", value: shortDate(ads.last_change_at, { now }) });
    if (act.audit_line) accountRows.push({ key: "audit", label: "Last audit", value: act.audit_line });
    if (ads.cm_name) accountRows.push({ key: "cm", label: "Managed by", value: ads.cm_name });
  }

  const reachRows = [];
  if (delivery) {
    const head = [`${commas(delivery.impressions)} ${delivery.impressions === 1 ? "impression" : "impressions"}`];
    if (delivery.clicks) head.push(`${commas(delivery.clicks)} ${delivery.clicks === 1 ? "click" : "clicks"}`);
    if (delivery.engagements) head.push(`${commas(delivery.engagements)} ${delivery.engagements === 1 ? "engagement" : "engagements"}`);
    if (delivery.leads) head.push(plural(delivery.leads, "lead"));
    reachRows.push({ key: "reach", label: "Our ads", value: head.join(", "), sub: monthSpan(delivery.first_month, delivery.last_month) });
  }

  // The frozen HubSpot facts, by the Profile registry's own reading of them, so the
  // two places can never name a field differently.
  const hubspotRows = PROFILE_FIELDS.filter((f) => f.group === "hubspot")
    .map((f) => ({ key: f.key, label: f.label, value: f.read({ contact }) }))
    .filter((r) => !blank(r.value));

  const first = ads
    ? [budgetWords(ads.monthly_budget, ads.currency) ? `${budgetWords(ads.monthly_budget, ads.currency)} budget` : "Ad account linked", Number(ads.active_campaigns) ? plural(Number(ads.active_campaigns), "live campaign") : null].filter(Boolean).join(" · ")
    : /^y/i.test(String(running || "")) ? "Running ads · no ad account linked" : "No ad account linked";
  const second = situation === "new_first_email" ? null : intake.short || "Campaign: not captured";
  return { state, summary: [first, delivery ? "our ads reach them" : null, second].filter(Boolean).join(" · "), accountRows, reachRows, hubspotRows, intake };
}

/* ================================================================== behind the scenes */

// The short names the one line uses ("Auto-reply On, didn't run").
const SHORT = { after_hours_auto_reply: "Auto-reply", call_reminders: "Pre-call reminders", call_no_show: "No-show rescue" };
const VERB = { after_hours_auto_reply: "Auto-replied", call_reminders: "Sent a reminder", call_no_show: "Sent the no-show note" };

/**
 * The switch state, in a neutral outline (the brief: no green "On" pills).
 * OFF beats everything (a standing decision about this company), then whether it
 * is armed at all, then dry run: the same order AutomationCard read them in.
 */
export function automationState(a) {
  if (!a) return { key: "draft", label: "Draft, not running", short: "draft" };
  if (a.off) return { key: "off", label: "Off for this company", short: "off for this company" };
  if (!a.enabled) return { key: "draft", label: "Draft, not running", short: "draft" };
  if (a.dry_run) return { key: "dry", label: "Dry run, nothing sent", short: "dry run" };
  return { key: "on", label: "On", short: "On" };
}

/**
 * What it last did here, as a sentence ("Auto-replied Sep 12, scored 52") and as the
 * one line's clause ("ran Sep 12"). From last_run (auto_reply_logs, PR #155); null
 * when it never acted on this company.
 */
export function lastRunWords(a, { now = new Date() } = {}) {
  const r = a && a.last_run;
  if (!r) return null;
  const day = r.at ? shortDate(r.at, { now }) : null;
  const on = day ? ` ${day}` : "";
  const scored = r.score !== null && r.score !== undefined ? `, scored ${r.score}` : "";
  const verb = VERB[a.type] || "Ran";
  switch (r.outcome) {
    case "QUEUED":
      return { text: `Queued to go${r.scheduled_send_at ? ` ${shortDate(r.scheduled_send_at, { now })}` : ""}${scored}`, short: "queued" };
    case "REPLIED":
      return { text: `${verb}${on}, they replied${scored}`, short: `ran${on}` };
    case "SCHEDULED":
      return { text: `${verb}${on}, they booked${scored}`, short: `ran${on}` };
    case "BOUNCED":
      return { text: `${verb}${on}, it bounced${scored}`, short: `bounced${on}` };
    case "BLOCKED":
      return { text: `Held back${on}${r.blocked_reason ? `: ${r.blocked_reason}` : ""}${scored}`, short: `held back${on}` };
    case "CANCELLED":
      return { text: `Cancelled${on}${scored}`, short: `cancelled${on}` };
    case "DRY_RUN":
      return { text: `Dry run${on}, nothing sent${scored}`, short: `dry run${on}` };
    default:
      return { text: `${verb}${on}${scored}`, short: `ran${on}` };
  }
}

/** "The after-hours inbound auto-reply doesn't answer pluto.security, a client's domain, so it's left out." */
function leftOutSentence(a) {
  const name = String(a.name || SHORT[a.type] || "This automation").trim();
  return `${/^the /i.test(name) ? sentence(name) : `The ${name.charAt(0).toLowerCase()}${name.slice(1)}`} ${a.why_not}, so it’s left out.`;
}

const OUTCOME_WORDS = {
  QUEUED: "queued",
  PENDING: "sent",
  DELIVERED: "sent",
  REPLIED: "sent, they replied",
  SCHEDULED: "sent, they booked",
  GHOSTED: "sent, no reply",
  BOUNCED: "bounced",
  BLOCKED: "skipped",
  CANCELLED: "cancelled",
  DRY_RUN: "dry run",
};

/**
 * Behind the scenes:
 *   behindView({ automations, show, company, booking, form, now })
 *     → { acting, leftOut, plans, summary, booking, audit }
 *
 * ONLY WHAT CAN ACT NOW. `applies` is the server's answer about the company (PR
 * #155, src/automations/applies.js), never worked out here; the switches (On, off
 * for this company, draft, dry run) are read beside it, so an automation switched
 * off for this company keeps its row, and its "Turn back on". What does not apply
 * is said once, in a sentence, in the open body: "The after-hours inbound
 * auto-reply doesn't answer pluto.security, a client's domain, so it's left out."
 *
 * The after-hours reply answers the website form and nothing else (reality.txt
 * [20]), so on a lead whose form came in and got nothing the line says "didn't
 * run", and on anyone else "form only", never "if they write".
 *
 * The booking link is for prospects and parked companies, the audited flag for
 * prospects past their first email (situation.mjs behindScenesShows); the line
 * names them as they stand ("booking link ready", "not audited").
 */
export function behindView({ automations = null, show = {}, company = null, booking = null, form = null, now = new Date() } = {}) {
  const list = (automations && automations.automations) || [];
  const acting = list
    .filter((a) => a && a.applies)
    .map((a) => {
      const state = automationState(a);
      const ran = lastRunWords(a, { now });
      return {
        id: a.automation_id,
        type: a.type,
        name: a.name || SHORT[a.type] || "Automation",
        short: SHORT[a.type] || a.name || "Automation",
        state,
        runsWhen: a.runs_when ? `${sentence(a.runs_when)}.` : null,
        lastRun: ran ? ran.text : null,
        lastShort: ran ? ran.short : null,
        off: !!a.off,
        offLine: a.off ? [a.note ? sentence(a.note) : null, a.off_since ? `off since ${shortDate(a.off_since, { now })}` : "off for this company"].filter(Boolean).join(" · ") : null,
      };
    });
  const leftOut = list.filter((a) => a && !a.applies && a.why_not).map(leftOutSentence);

  // The reminder plans for calls still ahead: what a Skip can still change.
  const reminderIds = new Set(acting.filter((a) => a.type === "call_reminders").map((a) => String(a.id)));
  const plans = ((automations && automations.plans) || [])
    .filter((p) => p && reminderIds.has(String(p.automation_id)) && toDate(p.call_at) > (toDate(now) || new Date()))
    .sort((a, b) => toDate(a.call_at) - toDate(b.call_at))
    .slice(0, 3)
    .map((p) => ({
      plan: p,
      id: p.id,
      title: p.call_title || "Booked call",
      skipped: p.status === "skipped",
      stopped: p.status === "stopped",
      canSkip: p.status === "active" || p.status === "skipped",
      note: p.status === "skipped" ? p.stopped_reason || "Skipped for this call" : p.status === "stopped" ? p.stopped_reason || "Stopped" : null,
      reminders: (p.reminders || []).map((r) => ({ key: r.step_index, at: r.at, outcome: OUTCOME_WORDS[r.outcome] || String(r.outcome || "").toLowerCase(), blocked: r.blocked_reason || null })),
    }));

  const parts = acting.map((a) => {
    if (a.state.key !== "on") return `${a.short} ${a.state.short}`;
    const clause = a.lastShort || (a.type === "after_hours_auto_reply" ? (form ? "didn’t run" : "form only") : null);
    return `${a.short} On${clause ? `, ${clause}` : ""}`;
  });
  const bookingPart = show.booking ? (booking && booking.url ? "booking link ready" : "booking link not set up") : null;
  const auditPart = show.audit && company ? (company.audited_at ? `audited ${shortDate(company.audited_at, { now })}` : "not audited") : null;
  if (bookingPart) parts.push(bookingPart);
  if (auditPart) parts.push(auditPart);
  const name = (company && company.name) || "this record";
  return {
    acting,
    leftOut,
    plans,
    summary: parts.length ? parts.join(" · ") : `Nothing acts on ${name} now`,
    booking: show.booking ? booking || null : null,
    audit: !!(show.audit && company),
  };
}

/* ================================================================== deal and money */

/**
 * The client's budget question, split so its words can be bold and its Edit a link:
 * "On a client this field means our retainer. Is this our retainer?" →
 * { lead: "On a client this field means our retainer.", question: "Is this our retainer?" }.
 */
export function askParts(ask) {
  const s = String(ask || "").trim();
  if (!s) return null;
  const m = s.match(/^(.*?)\s*(Is this our retainer\?)\s*$/);
  return m ? { lead: m[1] || null, question: m[2] } : { lead: s, question: null };
}

/** "source unknown" → "Source unknown.", "From the Sep 18 call" → "From the Sep 18 call." */
export function asSentence(text) {
  const s = sentence(text);
  if (!s) return s;
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/*
 * WHICH ROWS A SITUATION SHOWS. Before anything was priced (a new lead, a booked
 * or held call) the fee, the agreement and the win are not news, and six "not
 * recorded" rows on a lead who arrived this morning would bury the one figure that
 * matters there, their budget (the matrix: "Their budget, if told", "Their budget
 * from the call"). From Pricing sent on, every row shows, empty ones once each.
 */
const EARLY = new Set(["new_first_email", "new_chasing", "call_booked", "call_held"]);
export function dealRowsShown(rows, situation) {
  const all = rows || [];
  if (!EARLY.has(situation)) return all;
  return all.filter((r) => r.key === "budget" || (r.key === "quoted" && !r.empty));
}

/** The line when dealSummary has nothing to carry: early on, the one figure it waits for. */
export function dealEmptySummary(situation) {
  return EARLY.has(situation) ? "Their budget not recorded" : "Nothing recorded yet";
}
